import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { routeSeo } from "@/content/seo";

const pillars = [
  {
    title: "Visual composition",
    status: "pilot" as const,
    body: "Software is composed on a canvas of rounded-square tiles. Each tile is a real engineering capability with a type, configuration, inputs and outputs, inspectable at a glance rather than a picture that only looks like software.",
  },
  {
    title: "Connections describe relationships",
    status: "pilot" as const,
    body: "A connection between two tiles is the real path data or control takes between them. Connections are drawn locally, between tiles that actually relate, not as page-length decoration.",
  },
  {
    title: "Local execution",
    status: "pilot" as const,
    body: "A deterministic local Runtime plans, schedules and executes tile graphs as real operating-system processes across multiple languages, journals every run, and recovers from ordinary failures.",
  },
  {
    title: "Cloud-connected canvases",
    status: "in-development" as const,
    body: "The same graph model is designed to move between a local canvas and Devory Cloud's workspaces. Binding local and cloud canvases into one consistent experience is active, ongoing work.",
  },
];

export function CanvasPage() {
  const seo = routeSeo["/canvas"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/canvas" />

      <section className="container page-hero">
        <p className="eyebrow">Canvas</p>
        <h1 className="page-hero-title">A visual engineering canvas, wired to a real Runtime</h1>
        <p className="page-hero-lede">
          The canvas is where components become graphs, workflows and connected systems. It is a
          place Devory shows you a system, not a room every page traps you inside.
        </p>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="connect" size="xl" state="active" />
      </section>

      <section className="container section" aria-labelledby="canvas-pillars-heading">
        <SectionHeading
          level={2}
          title={<span id="canvas-pillars-heading">How the canvas works</span>}
        />
        <div className="grid-2">
          {pillars.map((pillar) => (
            <GlassPanel as="article" key={pillar.title} className="audience-card">
              <div className="capability-card-head">
                <h3>{pillar.title}</h3>
                <StatusBadge status={pillar.status} />
              </div>
              <p>{pillar.body}</p>
            </GlassPanel>
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="canvas-limits-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="canvas-limits-heading">What the canvas does not do today</span>}
          />
          <ul className="list-check">
            <li>
              Execution is limited to trusted, known components. No untrusted code sandbox exists
              yet
            </li>
            <li>Browser-based execution is policy-defined but not wired to an executor</li>
            <li>
              An automated crash-resume worker does not yet exist. A stalled run is retriable, not
              auto-resumed
            </li>
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>See how components become a canvas</h2>
          <p>The canvas is built from components. Read how those are defined, tested and reused.</p>
          <div className="cta-banner-actions">
            <CtaLink to="/components" variant="secondary">
              Read about components
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
